import { ITrackedClass } from '@models/interfaces/tracked-class';
import { Kb3dModels } from './decorators';
import { Kb3dObject } from './kb3d-object';

export type KeyOfAndEmpty<T> = keyof T | '';

export class Validator {
    protected vmap = new Map<string, Map<string, string[]>>();

    public getAll() {
        return this.vmap;
    }

    public validateNode<T extends Kb3dObject>(node: ITrackedClass<T>) {
        const model = Kb3dModels.get(node._token);
        if (model && model.validate) {
            model.validate(node, this);
        }
    }

    public nodeIsValid(node: Kb3dObject) {
        return (
            !this.vmap.has(node.id) ||
            !this.vmap.get(node.id)!.size ||
            [...this.vmap.get(node.id)!.values()].every(a => a == null || a.length == 0)
        );
    }

    public getErrors(nodeId: string, prop: string) {
        return this.vmap.get(nodeId)?.get(prop as string);
    }

    /** removes all validation errors for the given property */
    public clearProp<T extends Kb3dObject>(node: T, prop: KeyOfAndEmpty<T>) {
        if (this.vmap.has(node.id) && this.vmap.get(node.id)!.has(prop as string)) {
            this.vmap.get(node.id)?.delete(prop as string);
        }
        return this;
    }

    /** removes a specific validation errors for the given property */
    public clearPropError<T extends Kb3dObject>(node: T, prop: KeyOfAndEmpty<T>, ...errors: string[]) {
        return this.clearPropErrorWhere(node, prop, e => e.equalsAny(...errors));
    }

    public clearPropErrorWhere<T extends Kb3dObject>(
        node: T,
        prop: KeyOfAndEmpty<T>,
        predicate: (error: string) => boolean
    ) {
        if (this.vmap.has(node.id) && this.vmap.get(node.id)!.has(prop as string)) {
            this.vmap
                .get(node.id)
                ?.get(prop as string)
                ?.removeWhere(e => predicate(e));
        }
        return this;
    }

    public clearNode<T extends Kb3dObject>(node: T) {
        this.vmap.delete(node.id);
    }

    public addError<T extends Kb3dObject>(node: T, prop: KeyOfAndEmpty<T>, ...errors: string[]) {
        if (!this.vmap.has(node.id)) {
            this.vmap.set(node.id, new Map<string, string[]>());
        }
        const entry = this.vmap.get(node.id)!;
        const sprop = prop as string;
        if (!entry.has(sprop)) {
            entry.set(sprop, [...errors]);
        } else {
            const existingErrors = entry.get(sprop)!;
            for (const err of errors) {
                if (!existingErrors.contains(err)) {
                    existingErrors.push(err);
                }
            }
        }
        return this;
    }

    public assert<T extends Kb3dObject>(node: T, prop: KeyOfAndEmpty<T>, expression: boolean, ...errors: string[]) {
        if (expression) {
            this.clearPropError(node, prop, ...errors);
        } else {
            this.addError(node, prop, ...errors);
        }
        return this;
    }
}
